import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { Icon, Modal } from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import ArbitrageGlobe from "./ArbitrageGlobe.jsx";
import { regionFlag, regionLabel, routeKey, routesFromLegs } from "./arbitrageRegions.js";
import ArbitrageLiveData from "./ArbitrageLiveData.jsx";
import "./arbitrage-globe.scss";

/**
 * The popup a route card opens: every geographic arbitrage for that reference
 * on a globe, with the full list beside it (Alex, 2026-10-12).
 *
 * Instant by construction — the routes are worked out in the browser from the
 * legs the card already carries (routesFromLegs mirrors the nightly job), and
 * the world map is the shared atlas the grid preloads. Opening makes no
 * request.
 *
 * Modal animates in on its own; it unmounts the moment `open` goes false, so
 * the exit is played here: the page keeps it mounted for CLOSE_MS with an
 * `is-closing` class, then lets go.
 */

const POOL = { trade: "Trade", market: "Market" };
const TYPE = { trade: "Trade → Trade", market: "Market → Market", cross: "Trade ↔ Market" };

const money = (minor, currency) => {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: String(currency || "gbp").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(minor / 100);
  } catch {
    return Math.round(minor / 100).toLocaleString();
  }
};

const Flag = ({ region }) => {
  const code = regionFlag(region);
  return code ? (
    <ReactCountryFlag countryCode={code} svg className="arb-flag" aria-hidden="true" />
  ) : (
    <Icon name="Globe" size="sm" className="arb-flag arb-flag--globe" aria-hidden="true" />
  );
};

const LegCell = ({ leg, currency }) => (
  <span className="arb-gm__leg">
    <Flag region={leg.region} />
    <span className="arb-gm__leg-where">
      {POOL[leg.pool]} · {regionLabel(leg.region)}
    </span>
    <span className="arb-gm__leg-price">{money(leg.median, currency)}</span>
  </span>
);

const ArbitrageGlobeModal = ({ route, view, currency, closing, onClose, storeIdM, months }) => {
  const initialKey = route
    ? routeKey({
      buy: { pool: route.buy_pool, region: route.buy_region },
      sell: { pool: route.sell_pool, region: route.sell_region },
    })
    : null;
  const [focusKey, setFocusKey] = useState(initialKey);

  // A different card reuses the mounted popup; start from ITS route.
  useEffect(() => setFocusKey(initialKey), [initialKey]);

  // Keep the selected route visible in the list, whether it was picked there
  // or by tapping a line on the globe.
  const listRef = useRef(null);
  // Only inside the list's OWN scroll box (laptop). On a phone the list is
  // part of the sheet, and scrolling it would carry the globe out of view.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector(".arb-gm__route.is-on");
    if (!list || !row || list.scrollHeight <= list.clientHeight) return;
    const top = row.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = top - list.clientHeight / 2 + row.offsetHeight / 2;
    }
  }, [focusKey]);

  const supplyView = view === "supply_pressure";
  const routes = useMemo(() => {
    const value = (r) => (supplyView ? r.score : r.gapPercent);
    return routesFromLegs(route?.legs || []).sort((a, b) => value(b) - value(a));
  }, [route, supplyView]);

  // The buy side's most-often-cheapest trade source, computed nightly per leg.
  const focused = routes.find((r) => routeKey(r) === focusKey);
  const focusedSource = focused?.buy.pool === "trade" ? focused.buy.source : null;

  if (!route) return null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      className={`arb-gm${closing ? " is-closing" : ""}`}
      title={
        <span className="arb-gm__title">
          <span className="arb-gm__media">
            {route.image_file_id ? (
              <img src={getFileUrl(route.image_file_id)} alt="" />
            ) : (
              <span className="arb-card__media-empty" aria-hidden="true" />
            )}
          </span>
          <span className="arb-gm__name">
            <span className="arb-gm__reference">{route.reference}</span>
            <span className="arb-gm__model">{route.brand} · {route.model}</span>
          </span>
        </span>
      }
    >
      <div className="arb-gm__body">
        <div className="arb-gm__map">
          <ArbitrageGlobe
            legs={route.legs || []}
            routes={routes}
            focusKey={focusKey}
            view={view}
            currency={currency}
            onFocus={setFocusKey}
          />
          <p className="arb-gm__key">
            <span className="arb-gm__dot is-buy" /> buy
            <span className="arb-gm__dot is-sell" /> sell
            <span className="arb-gm__dot" /> other
            <span>· T / M = trade / market</span>
          </p>
        </div>

        <div className="arb-gm__list">
          {focusedSource && (
            <p className="arb-gm__source">
              Cheapest source in {regionLabel(focused.buy.region)} trade: <b>{focusedSource.name}</b>, cheapest on{" "}
              {focusedSource.share}% of its {focusedSource.n} quotes
            </p>
          )}
          <p className="arb-gm__count">
            {routes.length} route{routes.length === 1 ? "" : "s"}
          </p>
          <ul ref={listRef}>
            {routes.map((r) => {
              const key = routeKey(r);
              const on = key === focusKey;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className={`arb-gm__route${on ? " is-on" : ""}`}
                    aria-pressed={on}
                    onClick={() => setFocusKey(key)}
                  >
                    <span className="arb-gm__route-type">{TYPE[r.type]}</span>
                    <span className="arb-gm__route-legs">
                      <LegCell leg={r.buy} currency={currency} />
                      <Icon name="ArrowRight" size="sm" aria-hidden="true" />
                      <LegCell leg={r.sell} currency={currency} />
                    </span>
                    <span className="arb-gm__route-gap">
                      +{r.gapPercent}%
                      <small>{supplyView ? `score ${r.score}` : money(r.gap, currency)}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* The price points behind the selected route, and every price of the
          watch, with who listed each (Alex, 2026-10-21). */}
      <ArbitrageLiveData productId={route.product_id} storeIdM={storeIdM} months={months} focus={focused || null} />
    </Modal>
  );
};

export default ArbitrageGlobeModal;
